import React, { useEffect, useRef, useState } from "react";
import AvatarImage from "./AvatarImage";
import { Link, useLocation, useNavigate } from "react-router-dom";
import SelectUser from "./SelectUser";
import DeleteAccount from "./DeleteAccount";
import LogOut from "./LogOut";
import { LANGUAGES, ROLE, DYNAMIC_VARIABLES, SOCKET_EVENTS } from "../../../utils/Constants";
import { SOCKET_CLIENT } from "../../../utils/socket";
import i18next from "i18next";
import { useDispatch, useSelector } from "react-redux";
import { changeLang } from "../../../redux/slices/globalDetails";
import { addProfileData } from "../../../redux/slices/profileslice";
import { getUser, TrimText, setLocalKey, secToHours, isGuest, isUserMobileMissing } from "../../../utils/commonfunction";

import { profileDetails, getMyInvitations } from "../services/user.service";
import { getNotifications } from "../services/stream.service";
import { useTranslation } from "react-i18next";
import { Placeholder } from "rsuite";
import Swal from "sweetalert2";
import { BriefcaseBusiness, LayoutDashboardIcon, ShoppingBagIcon, ShoppingCartIcon, TvMinimalPlayIcon, Users, Clock, MousePointer2, ReceiptText, Video, X, Bookmark } from "lucide-react";


function Header() {
  const reduxUserData = useSelector((state) => state.profile.profileData);
  const [userData, setUserData] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [staffInvitationsCount, setStaffInvitationsCount] = useState(() => {
    try {
      const cached = localStorage.getItem("staff_invitations_count");
      return cached !== null ? Number(cached) : 0;
    } catch {
      return 0;
    }
  });

  const location = useLocation();
  const { t } = useTranslation();
  const animationContainerRef = useRef();
  const navigate = useNavigate();

  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isLangOpen, setIsLangOpen] = useState(false);
  const [isTutorialModalOpen, setIsTutorialModalOpen] = useState(false);
  const [tutorialModalType, setTutorialModalType] = useState("product"); // "product" | "youtube" | "twitch"
  const profileRef = useRef(null);
  const langRef = useRef(null);

  useEffect(() => {
    const preventScroll = (e) => {
      if (isProfileOpen || isLangOpen) {
        // Allow scrolling if the mouse is inside the dropdown containers
        if (profileRef.current?.contains(e.target) || langRef.current?.contains(e.target)) {
          return;
        }
        e.preventDefault();
      }
    };
    if (isProfileOpen || isLangOpen) {
      window.addEventListener("wheel", preventScroll, { passive: false });
      window.addEventListener("touchmove", preventScroll, { passive: false });
    }
    return () => {
      window.removeEventListener("wheel", preventScroll);
      window.removeEventListener("touchmove", preventScroll);
    };
  }, [isProfileOpen, isLangOpen]);

  useEffect(() => {
    let userRole = ROLE.USER;
    if (window.location.href.includes(`/${ROLE.ADVERTISER}`)) {
      userRole = ROLE.USER;
    }
    let data = getUser(userRole);

    // Fallback: If on an advertiser route but no advertiser data exists, 
    // check if a regular user is logged in (for the Become Advertiser flow)
    //     if ((!data || Object.keys(data).length === 0) && userRole === ROLE.ADVERTISER) {
    //       const buyerData = getUser(ROLE.USER);
    //       if (buyerData && Object.keys(buyerData).length > 0) {
    //         data = buyerData;
    //       }
    //     }
    // 
    if (isGuest()) {
      setUserData({
        name: t("header.guest_user"),
        profile_image: "/images/user-profile.png",
        role: ROLE.USER,
        isGuest: true
      });
      setStaffInvitationsCount(0);
      try { localStorage.removeItem("staff_invitations_count"); } catch {}
      return;
    }

    const currentData = data && Object.keys(data).length > 0 ? data : null;
    setUserData(currentData);

    // Fetch latest fresh user details from backend to ensure accurate header state
    if (currentData && currentData._id && currentData.token) {
      profileDetails({ _id: currentData._id })
        .then((res) => {
          if (res && res.data) {
            const freshData = { ...currentData, ...res.data };
            setUserData(freshData);
            setLocalKey(ROLE.USER, JSON.stringify(freshData));
            if (freshData.isAdvertiser || freshData.role === ROLE.ADVERTISER) {
              setLocalKey(ROLE.ADVERTISER, JSON.stringify(freshData));
            }
          }
        })
        .catch((err) => console.log("Failed fetching profile details in Header:", err));

      getNotifications()
        .then((res) => {
          if (res && res.success) {
            const dataObj = res.data || {};
            if (typeof dataObj.unreadCount === "number") {
              setUnreadCount(dataObj.unreadCount);
            } else {
              const list = Array.isArray(dataObj.notifications)
                ? dataObj.notifications
                : (Array.isArray(dataObj.list)
                  ? dataObj.list
                  : (Array.isArray(dataObj) ? dataObj : []));
              const unread = list.filter((n) => !n.is_read).length;
              setUnreadCount(unread);
            }
          }
        })
        .catch((err) => console.log("Failed fetching notifications:", err));

      getMyInvitations()
        .then((res) => {
          if (res && res.success && Array.isArray(res.data)) {
            const count = res.data.length;
            setStaffInvitationsCount(count);
            try { localStorage.setItem("staff_invitations_count", String(count)); } catch {}
          }
        })
        .catch((err) => {
          console.log("Failed fetching invitations in Header:", err);
          // Preserve cached count on network error to prevent flickering
        });
    } else {
      setStaffInvitationsCount(0);
      try { localStorage.removeItem("staff_invitations_count"); } catch {}
    }
  }, [location.pathname]);

  useEffect(() => {
    const handleNotificationUpdate = () => {
      getNotifications()
        .then((res) => {
          if (res && res.success) {
            const dataObj = res.data || {};
            if (typeof dataObj.unreadCount === "number") {
              setUnreadCount(dataObj.unreadCount);
            } else {
              const list = Array.isArray(dataObj.notifications)
                ? dataObj.notifications
                : (Array.isArray(dataObj.list)
                  ? dataObj.list
                  : (Array.isArray(dataObj) ? dataObj : []));
              const unread = list.filter((n) => !n.is_read).length;
              setUnreadCount(unread);
            }
          }
        })
        .catch((err) => console.log("Failed fetching notifications on update:", err));
    };

    window.addEventListener("NEW_NOTIFICATION_RECEIVED", handleNotificationUpdate);
    return () => window.removeEventListener("NEW_NOTIFICATION_RECEIVED", handleNotificationUpdate);
  }, []);

  useEffect(() => {
    if (reduxUserData && Object.keys(reduxUserData).length > 0) {
      setUserData((prev) => ({ ...prev, ...reduxUserData }));
    }
  }, [reduxUserData]);

  useEffect(() => {
    const handleBalanceUpdate = (newBalance) => {
      if (newBalance) {
        setUserData((prev) => ({
          ...prev,
          ...newBalance,
        }));
      }
    };
    SOCKET_CLIENT.on(SOCKET_EVENTS.AI_CLICKS_UPDATED, handleBalanceUpdate);
    return () => {
      SOCKET_CLIENT.off(SOCKET_EVENTS.AI_CLICKS_UPDATED, handleBalanceUpdate);
    };
  }, []);

  const [globalData, setGlobalData] = useState({});
  const myGlobalData = useSelector((state) => state.globalData);
  useEffect(() => {
    if (myGlobalData && myGlobalData?.data) {
      const settingsData = JSON.parse(myGlobalData?.data);
      setGlobalData(settingsData);
    }
  }, [myGlobalData]);

  const getTutorialVideoUrl = () => {
    let rawVid = "";
    if (tutorialModalType === "youtube") {
      rawVid = globalData?.youtube_tutorial_video || "";
    } else if (tutorialModalType === "twitch") {
      rawVid = globalData?.twitch_tutorial_video || "";
    } else {
      rawVid = globalData?.create_product_tutorial_video || globalData?.video_link || "";
    }
    if (!rawVid) return "";
    return rawVid.startsWith('http') || rawVid.startsWith('blob:')
      ? rawVid
      : `${DYNAMIC_VARIABLES.SERVER_URL}/uploads/settings/${rawVid}`;
  };

  const tutorialVideoUrl = getTutorialVideoUrl();

  const [type] = useState("");
  // const BASE = `/${userData?.role}` || 'user';
  const BASE = userData?.role ? `/${userData.role}` : "/user";
  const [selectedLang, setSelectedLang] = useState("en");
  const triggerRef = useRef(null);
  const Dispatch = useDispatch();

  useEffect(() => {
    const storedLang = localStorage.getItem("i18nextLng") || "en";
    setSelectedLang(storedLang);
    i18next.changeLanguage(storedLang);
  }, []);

  const changeLanguage = (lng) => {
    const bodyElem = document.querySelector("body");
    if (bodyElem) {
      bodyElem.classList.add("lang-animation");
      animationContainerRef.current.classList.add("animate");
    }
    setTimeout(() => {
      setSelectedLang(lng);
      localStorage.setItem("i18nextLng", lng);
      Dispatch(changeLang(lng));
      i18next.changeLanguage(lng);
    }, 300);
    setTimeout(() => {
      if (triggerRef.current) {
        triggerRef.current.focus();
        triggerRef.current.blur();
      }
    }, 0);
    setTimeout(() => {
      if (bodyElem) {
        bodyElem.classList.remove("lang-animation");
        setTimeout(() => {
          animationContainerRef.current.classList.remove("animate");
        }, 1000);
      }
    }, 1000);
  };

  const handleNavClick = (e, item) => {
    if (isUserMobileMissing(userData)) {
      e.preventDefault();
      Swal.fire({
        icon: "warning",
        title: t("common.mobile_number_required", { defaultValue: "Mobile Number Required" }),
        text: t("common.please_add_mobile_number_to_continue", { defaultValue: "Please add your mobile number to continue." }),
        confirmButtonColor: "var(--primary-400, #ff4081)",
        confirmButtonText: "Add Mobile Number",
      }).then(() => {
        navigate("/user/profile-edit");
      });
      return;
    }
    if (item?.onClick) item.onClick(e);
  };

  const RAW_USER_NAV_ITEMS = [
    { label: t("header.home"), to: "/" },
    ...(String(globalData?.show_business_streams) === "true"
      ? [{ label: t("header.streams"), to: "/user/live-streams" }]
      : []),
    { label: t("header.products"), to: "/user/search" },
    // ...(userData?.token && staffInvitationsCount > 0
    //   ? [{ label: t("header.assigned_streams", "Assigned Streams"), to: "/user/assigned-streams" }]
    //   : []),
  ];

  const USER_NAV_ITEMS = RAW_USER_NAV_ITEMS;

  const RAW_ADVERTISER_NAV_ITEMS = [
    { label: t("header.dashboard"), to: "/advertiser/dashboard" },
    ...(String(globalData?.show_business_streams) === "true"
      ? [{ label: t("header.streams"), to: "/advertiser/streams" }]
      : []),
    { label: t("header.products"), to: "/advertiser/products" },
    // ...(staffInvitationsCount > 0
    //   ? [{ label: t("header.assigned_streams", "Assigned Streams"), to: "/user/assigned-streams" }]
    //   : []),
  ];

  const ADVERTISER_NAV_ITEMS = RAW_ADVERTISER_NAV_ITEMS;

  const USER_ITEMS = [];
  USER_ITEMS.push({ label: t("header.edit_profile"), to: `${BASE}/profile-edit`, image: "/images/my-a-icon1.svg" });
  USER_ITEMS.push({ label: t("header.change_password"), to: `${BASE}/change-password`, image: "/images/my-a-icon2.svg" });
  if (String(globalData?.show_favorites) === "true") USER_ITEMS.push({ label: t("header.favourites"), to: `${BASE}/favourites`, image: "/images/my-a-icon3.svg" });
  if (String(globalData?.show_favorites) === "true") USER_ITEMS.push({
    label: t("header.my_shoplist", "My Shoplist"),
    to: `${BASE}/shoplist`,
    icon: <Bookmark color="#494747ff" size={20} />
  });
  if (String(globalData?.show_my_activities) === "true") USER_ITEMS.push({ label: t("header.my_activity"), to: `${BASE}/my-activity`, image: "/images/my-a-icon4.svg" });
  if (String(globalData?.show_notification_settings) === "true") USER_ITEMS.push({ label: t("header.notification"), to: `${BASE}/notification-settings`, image: "/images/my-a-icon5.svg" });
  if (String(globalData?.show_access_invitations) === "true") USER_ITEMS.push({
    label: t("header.my_business"),
    to: "/user/staff-invitations",
    icon: <BriefcaseBusiness color="#494747ff" size={20} />
  });
  if (staffInvitationsCount > 0) {
    USER_ITEMS.push({
      label: t("header.assigned_streams", "Assigned Streams"),
      to: "/user/assigned-streams",
      icon: <TvMinimalPlayIcon color="#494747ff" size={20} />
    });
  }
  USER_ITEMS.push({
    label: t("header.delete_account"),
    image: "/images/my-a-icon6.svg",
    onClick: () => document.getElementById("delete_account_modal")?.showModal(),
  });
  USER_ITEMS.push({
    label: t("header.logout"),
    image: "/images/my-a-icon7.svg",
    onClick: () => document.getElementById("logout_modal")?.showModal(),
  });

  const ADVERTISER_ITEMS = [];
  ADVERTISER_ITEMS.push({ label: t("header.profile"), to: `${BASE}/profile`, image: "/images/my-a-icon1.svg" });
  ADVERTISER_ITEMS.push({ label: t("header.change_password"), to: `${BASE}/change-password`, image: "/images/my-a-icon2.svg" });
  if (String(globalData?.show_purchase_leads) === "true") ADVERTISER_ITEMS.push({ label: t("header.purchase_leads"), to: `${BASE}/purchase-leads`, image: "/images/my-a-icon3.svg" });
  if (String(globalData?.show_invoice) === "true") ADVERTISER_ITEMS.push({ label: t("header.invoice"), to: `${BASE}/invoice`, image: "/images/my-a-icon4.svg" });
  if (String(globalData?.show_my_teams) === "true") ADVERTISER_ITEMS.push({ label: t("header.my_team"), to: `${BASE}/my-team`, image: "/images/my-a-icon5.svg" });
  if (String(globalData?.show_packages) === "true") ADVERTISER_ITEMS.push({ label: t("header.packages"), to: `${BASE}/packages`, image: "/images/my-a-icon5.svg" });
  if (String(globalData?.show_package_history) === "true") ADVERTISER_ITEMS.push({ label: t("header.package_purchase_history"), to: `${BASE}/packages/purchase-history`, image: "/images/my-a-icon5.svg" });
  if (staffInvitationsCount > 0) {
    ADVERTISER_ITEMS.push({
      label: t("header.assigned_streams", "Assigned Streams"),
      to: "/user/assigned-streams",
      icon: <TvMinimalPlayIcon color="#494747ff" size={20} />
    });
  }
  ADVERTISER_ITEMS.push({
    label: t("header.product_tutorial", "Product Tutorial"),
    icon: <Video color="#494747ff" size={20} />,
    onClick: (e) => {
      e?.preventDefault();
      setIsProfileOpen(false);
      setTutorialModalType("product");
      setIsTutorialModalOpen(true);
    }
  });
  ADVERTISER_ITEMS.push({
    label: t("header.youtube_tutorial", "You Tube Setup Guide"),
    image: "/images/f02.png",
    onClick: (e) => {
      e?.preventDefault();
      setIsProfileOpen(false);
      setTutorialModalType("youtube");
      setIsTutorialModalOpen(true);
    }
  });
  ADVERTISER_ITEMS.push({
    label: t("header.twitch_tutorial", "Twitch Tutorial"),
    image: "/images/twitch.png",
    onClick: (e) => {
      e?.preventDefault();
      setIsProfileOpen(false);
      setTutorialModalType("twitch");
      setIsTutorialModalOpen(true);
    }
  });
  ADVERTISER_ITEMS.push({
    label: t("header.logout"),
    image: "/images/my-a-icon7.svg",
    onClick: () => {
      document.getElementById("logout_modal")?.showModal();
    },
  });

  // Advertiser routes injected into USER popup when isAdvertiser is true
  const RAW_ADVERTISER_ROUTE_ITEMS = [];
  if (String(globalData?.show_business_dashboard) === "true") RAW_ADVERTISER_ROUTE_ITEMS.push({ label: t("header.dashboard"), to: "/advertiser/dashboard", icon: <LayoutDashboardIcon color="#494747ff" size={20} /> });
  RAW_ADVERTISER_ROUTE_ITEMS.push({ label: t("header.business_profile"), to: "/advertiser/profile", image: "/images/my-a-icon1.svg" });
  if (String(globalData?.show_business_streams) === "true") RAW_ADVERTISER_ROUTE_ITEMS.push({ label: t("header.manage_streams", "Manage Streams"), to: "/advertiser/streams", icon: <TvMinimalPlayIcon color="#494747ff" size={20} /> });
  RAW_ADVERTISER_ROUTE_ITEMS.push({ label: t("header.products"), to: "/advertiser/products", icon: <ShoppingCartIcon color="#494747ff" size={20} /> });
  if (String(globalData?.show_purchase_leads) === "true") RAW_ADVERTISER_ROUTE_ITEMS.push({ label: t("header.purchase_leads"), to: "/advertiser/purchase-leads", icon: <ShoppingBagIcon color="#494747ff" size={20} /> });
  if (String(globalData?.show_invoice) === "true") RAW_ADVERTISER_ROUTE_ITEMS.push({ label: t("header.invoice"), to: "/advertiser/invoice", icon: <ReceiptText color="#494747ff" size={20} /> });
  if (String(globalData?.show_my_teams) === "true") RAW_ADVERTISER_ROUTE_ITEMS.push({ label: t("header.my_team"), to: "/advertiser/my-team", icon: <Users color="#494747ff" size={20} /> });
  if (String(globalData?.show_packages) === "true") RAW_ADVERTISER_ROUTE_ITEMS.push({ label: t("header.packages"), to: "/advertiser/packages", image: "/images/my-a-icon5.svg" });
  if (String(globalData?.show_package_history) === "true") RAW_ADVERTISER_ROUTE_ITEMS.push({ label: t("header.package_purchase_history"), to: "/advertiser/packages/purchase-history", image: "/images/my-a-icon5.svg" });
  RAW_ADVERTISER_ROUTE_ITEMS.push({
    label: t("header.product_tutorial", "Product Tutorial"),
    icon: <Video color="#494747ff" size={20} />,
    onClick: (e) => {
      e?.preventDefault();
      setIsProfileOpen(false);
      setTutorialModalType("product");
      setIsTutorialModalOpen(true);
    }
  });
  RAW_ADVERTISER_ROUTE_ITEMS.push({
    label: t("header.youtube_tutorial", "You Tube Setup Guide"),
    image: "/images/f02.png",
    onClick: (e) => {
      e?.preventDefault();
      setIsProfileOpen(false);
      setTutorialModalType("youtube");
      setIsTutorialModalOpen(true);
    }
  });
  RAW_ADVERTISER_ROUTE_ITEMS.push({
    label: t("header.twitch_tutorial", "Twitch Tutorial"),
    image: "/images/twitch.png",
    onClick: (e) => {
      e?.preventDefault();
      setIsProfileOpen(false);
      setTutorialModalType("twitch");
      setIsTutorialModalOpen(true);
    }
  });

  const ADVERTISER_ROUTE_ITEMS = RAW_ADVERTISER_ROUTE_ITEMS.filter(item => {
    if ((item.to === "/advertiser/streams" || item.label === t("header.streams") || item.label === t("header.manage_streams", "Manage Streams")) && String(globalData?.stream_tab_visibility) === "false") return false;
    // We are keeping the products tab visible as per user request
    return true;
  });

  // Build the final list: user items + optionally advertiser routes (before Logout)
  const baseItems = [...USER_ITEMS]; // always clone
  const logoutItem = baseItems.pop(); // pull Logout off the end
  let EFFECTIVE_USER_ITEMS = [];

  if (userData?.isGuest) {
    EFFECTIVE_USER_ITEMS = [logoutItem];
  } else {
    EFFECTIVE_USER_ITEMS = [...baseItems];

    if (userData?.isAdvertiser) {
      EFFECTIVE_USER_ITEMS.push({ label: "__divider__" });
      EFFECTIVE_USER_ITEMS.push(...ADVERTISER_ROUTE_ITEMS);
    } else {
      // Regular user
      if (userData?.isKycRegistered && !userData?.is_kyc_verified) {
        EFFECTIVE_USER_ITEMS.push({ label: "__divider__" });
        if (userData?.failed_verification_reason) {
          EFFECTIVE_USER_ITEMS.push({ label: "__rejected__" });
        } else {
          EFFECTIVE_USER_ITEMS.push({ label: "__awaiting__" });
        }
        EFFECTIVE_USER_ITEMS.push({
          label: t("header.edit_submitted_details", "Edit Submitted Details"),
          to: "/advertiser/profile",
          image: "/images/my-a-icon1.svg"
        });
      } else {
        EFFECTIVE_USER_ITEMS.push({ label: "__divider__" });
        EFFECTIVE_USER_ITEMS.push({ label: "__become_advertiser__" });
      }
    }

    EFFECTIVE_USER_ITEMS.push(logoutItem);
  }


  // Handle "Become Advertiser" click — show Swal confirm, then navigate to profile setup
  const handleBecomeAdvertiser = () => {
    if (!userData?.registration_status) {
      navigate(`/advertiser/otp-verification?user_id=${userData?._id}`);
      return;
    }

    Swal.fire({
      title: t("header.become_advertiser_title"),
      text: t("header.become_advertiser_text"),
      icon: "question",
      showCancelButton: true,
      confirmButtonText: t("header.yes_proceed"),
      cancelButtonText: t("header.cancel"),
      confirmButtonColor: "#FF7E79",
    }).then((result) => {
      if (result.isConfirmed) {
        navigate("/advertiser/profile-setup");
      }
    });
  };




  const closeModal = () => {
    const modal = document.getElementById('select_role_modal');
    if (modal) modal.close();
  };

  const closeLogOutModal = () => {
    const modal = document.getElementById("logout_modal");
    if (modal) modal.close();
  };

  const closeDeleteAccountModal = () => {
    const modal = document.getElementById("delete_account_modal");
    if (modal) modal.close();
  };

  const Navigate = useNavigate();

  const closeMobileDrawer = () => {
    const checkbox = document.getElementById("mobile-drawer");
    if (checkbox) checkbox.checked = false;
  };



  return (
    <>
      {(isProfileOpen || isLangOpen) && (
        <div 
          className="fixed inset-0 z-[45] bg-transparent cursor-default" 
          onClick={() => {
            setIsProfileOpen(false);
            setIsLangOpen(false);
          }}
        />
      )}
      <div className="lang-animation-container" ref={animationContainerRef}></div>
      <header className="bg-white py-[15px] fixed top-0 left-0 right-0 z-50 shadow-[0px_5px_14px_rgba(0,0,0,0.05)]">
        <div className="container">
          {/* DaisyUI Drawer Container */}
          <div className="drawer">
            <input id="mobile-drawer" type="checkbox" className="drawer-toggle" />
            <div className="drawer-content flex items-center gap-4">
              {/* Mobile Menu Button */}
              <label htmlFor="mobile-drawer" className="flex lg:hidden" aria-label="open sidebar">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </label>

              {/* Logo */}
              <Link to={userData?.role === "advertiser" ? "/advertiser/dashboard" : "/"} className="shrink-0 leading-none">
                <img src="/images/logo.svg" alt="logo" />
              </Link>

              {/* Search (desktop only) */}
              {/* {userData?.role === "user" &&(
                <div className="flex-1 hidden lg:flex max-w-[297px]">
                  <form className="w-full" onSubmit={handleSearch}>
                    <label htmlFor="global-search" className="sr-only"></label>

                    <div className="relative">
                

                      <button
                        type="submit"
                        className="w-[24px] h-[24px] absolute right-2 top-[8px] !right-[10px]"
                        aria-label="Search"
                      >
                        <img src="/images/search.svg" alt="Search" />
                      </button>
                    </div>
                  </form>
                </div>
              )}
        */}

              {/* Desktop Nav */}
              <nav className="hidden lg:flex items-center gap-6 ml-auto text-sm font-medium text-gray-700">
                {(userData?.role === "advertiser" ? ADVERTISER_NAV_ITEMS : USER_NAV_ITEMS).map((item, i) => (
                  <React.Fragment key={i}>
                    <Link
                      to={item.to}
                      onClick={(e) => handleNavClick(e, item)}
                      className="headings-h5 text-zinc-950 no-underline hover:!text-[var(--primary-400)]"
                    >
                      {item.label}
                    </Link>
                  </React.Fragment>
                ))}


                <div className="flex item-center gap-[10px]">
                  <div 
                    ref={langRef}
                    className={`dropdown ${isLangOpen ? "dropdown-open" : ""}`}
                  >
                    <div 
                      ref={triggerRef} 
                      role="button" 
                      className="flex items-center gap-2 border border-gray-200 rounded-[4px] px-[10px] py-[10px] text-gray-700 hover:bg-gray-50"
                      onClick={() => setIsLangOpen(!isLangOpen)}
                    >
                      <img src="/images/globe.svg" alt="globe" />

                      <span className="text-[14px] semibold">{LANGUAGES.find((x) => x.value === selectedLang)?.label}</span>

                      <img src="/images/drop-arrow.png" alt="drop-arrow" />
                    </div>

                    <ul tabIndex={0} className="dropdown-content menu bg-base-100 rounded-box z-1 w-52 p-2 shadow-sm" style={{ display: isLangOpen ? "block" : "none" }}>
                      {LANGUAGES.map((lang, i) => (
                        <li key={i} className="m-2">
                          <button onClick={() => {
                            changeLanguage(lang.value);
                            setIsLangOpen(false);
                          }}>{lang.label}</button>
                        </li>
                      ))}
                    </ul>
                  </div>
                  {(Array.isArray(userData) ? userData[0] : userData)?.token || (userData && userData.isGuest) ? (
                    <>
                      {userData?.token && (
                        <button
                          className="w-[42px] h-[42px] flex items-center justify-center rounded-full bg-[#FFC9C7] cp relative"
                          onClick={() => {
                            Navigate("/user/notifications");
                          }}>
                          <img src="/images/bell.svg" alt="bell" />
                          {unreadCount > 0 && (
                            <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-[10px] font-bold flex items-center justify-center rounded-full border-2 border-white shadow-sm z-10">
                              {unreadCount > 99 ? '99+' : unreadCount}
                            </span>
                          )}
                        </button>
                      )}


                      <div 
                        ref={profileRef}
                        className={`dropdown ${isProfileOpen ? "dropdown-open" : ""}`}
                      >
                        <div 
                          role="button" 
                          className="flex items-center gap-2 rounded-[4px] text-gray-700"
                          onClick={() => setIsProfileOpen(!isProfileOpen)}
                        >
                          <AvatarImage
                            src={userData?.profile_image}
                            name={userData?.name}
                            alt="user"
                            style={{ width: "45px", height: "45px", objectFit: "cover", borderRadius: "50%" }}
                          />
                          <span className="headings-h6 text-zinc-800">{userData && userData.name ? TrimText(userData.name, 30) : t("header.na")}</span>
                          <img src="/images/drop-arrow.png" alt="drop-arrow" />
                        </div>
                        <ul
                          tabIndex={0}
                          className="dropdown-content menu bg-white border border-zinc-100 rounded-xl z-[1] w-64 p-2 shadow-[0px_2px_24px_rgba(0,0,0,0.05)] space-y-[5px] left-auto right-0 custom-scrollbar dropdown-scroll-list overscroll-contain"
                          style={{
                            display: isProfileOpen ? "flex" : "none",
                            maxHeight: "70vh",
                            overflowY: "auto",
                            overflowX: "hidden",
                            flexDirection: "column",
                            flexWrap: "nowrap",
                            overscrollBehavior: "contain",
                          }}
                        >
                          {String(globalData?.show_user_stats) !== "false" && (userData?.isAdvertiser || userData?.role === "advertiser") && (
                            <div className="mx-1 mb-2 space-y-2">
                              <div className="px-3 py-2 bg-gradient-to-r from-orange-50 to-red-50 rounded-lg border border-orange-100 flex items-center justify-between shadow-sm">
                                <div className="flex items-center gap-1 flex-shrink-0">
                                  <Clock className="w-3.5 h-3.5 text-orange-500" />
                                  <span className="text-[11px] font-semibold text-orange-700 whitespace-nowrap">{t("header.hours")}:</span>
                                </div>
                                <span className="text-[12px] font-bold text-orange-600 whitespace-nowrap">
                                  {secToHours(userData?.remaining_hours || 0)}
                                </span>
                              </div>
                              <div className="px-3 py-2 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-100 flex items-center justify-between shadow-sm">
                                <div className="flex items-center gap-1">
                                  <MousePointer2 className="w-3.5 h-3.5 text-blue-500" />
                                  <span className="text-[11px] font-semibold text-blue-700">{t("header.ai_clicks")}:</span>
                                </div>
                                <span className="text-[12px] font-bold text-blue-600">
                                  {userData?.remaining_ai_clicks ?? 0}
                                </span>
                              </div>
                            </div>
                          )}
                          {(userData?.role === "advertiser" ? ADVERTISER_ITEMS : EFFECTIVE_USER_ITEMS).map((item, index) => (
                            item.label === "__divider__" ? (
                              // Divider + "Advertiser" label
                              <li key={`div-${index}`} className="!mt-1 !mb-0">
                                <div className="px-2 pt-2 pb-1 border-t border-zinc-100 mt-1">
                                  <span className="body-xs text-zinc-400 font-semibold uppercase tracking-widest">{t("header.business")}</span>
                                </div>
                              </li>
                            ) : item.label === "__divider__staff" ? (
                              // Divider + "Staff" label
                              <li key={`div-staff-${index}`} className="!mt-1 !mb-0">
                                <div className="px-2 pt-2 pb-1 border-t border-zinc-100 mt-1">
                                  <span className="body-xs text-zinc-400 font-semibold uppercase tracking-widest">{t("header.staff_access")}</span>
                                </div>
                              </li>
                            ) : item.label === "__rejected__" ? (
                              <li key={`rej-${index}`} className="!mt-0">
                                <div className="flex flex-col gap-1 px-2.5 py-2 bg-red-50/90 rounded-lg border border-red-200/80 my-1">
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-[6px]">
                                      <span className="body-sm text-red-700 font-semibold">Application Rejected</span>
                                    </div>
                                    <Link
                                      to="/advertiser/profile"
                                      onClick={() => setIsProfileOpen(false)}
                                      className="px-2 py-0.5 text-xs font-semibold text-white bg-red-500 hover:bg-red-600 rounded-md transition-colors !no-underline shadow-sm flex items-center gap-1"
                                    >
                                      <span>Fix & Reapply</span>
                                    </Link>
                                  </div>
                                  {userData?.failed_verification_reason && (
                                    <p className="text-[11px] text-red-600 mb-0 italic">Reason: {userData.failed_verification_reason}</p>
                                  )}
                                </div>
                              </li>
                            ) : item.label === "__awaiting__" ? (
                              <li key={`await-${index}`} className="!mt-0">
                                <div className="flex items-center justify-between px-2.5 py-2 bg-amber-50/80 rounded-lg border border-amber-200/70 my-1">
                                  <div className="flex items-center gap-[8px]">
                                    <img src="/images/my-a-icon4.svg" alt="pending" className="w-[18px]" />
                                    <span className="body-sm text-amber-700 font-semibold">{t("header.awaiting_approval")}</span>
                                  </div>
                                  <Link
                                    to="/advertiser/profile"
                                    onClick={() => setIsProfileOpen(false)}
                                    className="px-2.5 py-1 text-xs font-semibold text-white bg-[#FF7E79] hover:bg-[#ff6560] rounded-md transition-colors !no-underline shadow-sm flex items-center gap-1"
                                  >
                                    <span>{t("common.edit", "Edit")}</span>
                                  </Link>
                                </div>
                              </li>
                            ) : item.label === "__become_advertiser__" ? (
                              <li key={`become-${index}`} className="!mt-0">
                                <button
                                  onClick={() => {
                                    handleBecomeAdvertiser();
                                    setIsProfileOpen(false);
                                  }}
                                  className="flex items-center gap-[10px] no-underline hover:no-underline w-full text-left px-2 py-2"
                                >
                                  <img src="/images/my-a-icon4.svg" alt="become advertiser" className="w-[18px]" />
                                  <span className="body-sm text-[#FF7E79] medium font-semibold">{t("header.apply_for_business")}</span>
                                </button>
                              </li>
                            ) : (
                              <li key={index} className="!mt-0">
                                {item.to ? (
                                  <Link 
                                    to={item.to} 
                                    className="flex items-center gap-3 w-full text-left px-3 py-2 text-sm text-zinc-700 font-medium hover:bg-zinc-100/80 rounded-lg transition-colors !no-underline border-none bg-transparent"
                                    onClick={(e) => {
                                      setIsProfileOpen(false);
                                      if (item.to && item.to !== `${BASE}/profile-edit` && isUserMobileMissing(userData)) {
                                        e.preventDefault();
                                        Swal.fire({
                                          icon: "warning",
                                          title: t("common.mobile_number_required", { defaultValue: "Mobile Number Required" }),
                                          text: t("common.please_add_mobile_number_to_continue", { defaultValue: "Please add your mobile number to continue." }),
                                          confirmButtonColor: "var(--primary-400, #ff4081)",
                                          confirmButtonText: "Add Mobile Number",
                                        }).then(() => {
                                          navigate("/user/profile-edit");
                                        });
                                        return;
                                      }
                                      if (item.onClick) item.onClick(e);
                                    }}
                                  >
                                    <span className="w-5 h-5 flex items-center justify-center shrink-0">
                                      {item.icon ? (
                                        item.icon
                                      ) : (
                                        <img src={item.image} alt={item.label} className="w-4 h-4 object-contain" />
                                      )}
                                    </span>
                                    <span className="text-sm font-medium text-zinc-700 truncate">{item.label}</span>
                                  </Link>
                                ) : (
                                  <button 
                                    type="button"
                                    onClick={() => {
                                      item.onClick();
                                      setIsProfileOpen(false);
                                    }} 
                                    className="flex items-center gap-3 w-full text-left px-3 py-2 text-sm text-zinc-700 font-medium hover:bg-zinc-100/80 rounded-lg transition-colors border-none bg-transparent cursor-pointer"
                                  >
                                    <span className="w-5 h-5 flex items-center justify-center shrink-0">
                                      {item.icon ? (
                                        item.icon
                                      ) : (
                                        <img src={item.image} alt={item.label} className="w-4 h-4 object-contain" />
                                      )}
                                    </span>
                                    <span className="text-sm font-medium text-zinc-700 truncate">{item.label}</span>
                                  </button>
                                )}
                              </li>
                            )
                          ))}
                        </ul>
                      </div>
                    </>
                  ) : (
                    !isGuest() && (
                      <div className="login-signup flex items-center gap-[10px]">
                        <Link
                          className="btn btn-outline"
                          to="user/signup"
                        >
                          {t("header.signup")}
                        </Link>
                        <Link
                          className="btn btn-pri"
                          to="user/login"
                        >
                          {t("header.login")}
                        </Link>
                      </div>
                    )
                  )}

                </div>
              </nav>
            </div>

            <div className="drawer-side lg:hidden" style={{ zIndex: 100 }}>
              <label htmlFor="mobile-drawer" aria-label="close sidebar" className="drawer-overlay"></label>
              <ul className="menu p-4 w-80 min-h-full bg-white text-gray-700 flex flex-col gap-[10px]">
                {/* Close Button for mobile drawer */}
                <div className="flex justify-end mb-4">
                  <label htmlFor="mobile-drawer" className="p-2 cp">
                    <img src="/images/close.svg" alt="close" className="w-5 h-5" />
                  </label>
                </div>

                {/* Main Navigation Links */}
                {(userData?.role === "advertiser" ? ADVERTISER_NAV_ITEMS : USER_NAV_ITEMS).map((item, i) => (
                  <li key={i}>
                    <Link
                      to={item.to}
                      onClick={(e) => {
                        closeMobileDrawer();
                        handleNavClick(e, item);
                      }}
                      className="headings-h5 text-zinc-950 text-decoration-none hover:!text-[var(--primary-400)]"
                    >
                      {item.label}
                    </Link>
                  </li>
                ))}

                <div className="divider"></div>

                {/* Language Selector in Drawer */}
                <li>
                  <div className="dropdown p-0">
                    <div tabIndex={0} role="button" className="flex w-full items-center gap-2 border border-gray-200 rounded-[4px] px-[10px] py-[10px] text-gray-700 hover:bg-gray-50">
                      <img src="/images/globe.svg" alt="globe" />
                      <span className="text-[14px] semibold">
                        {LANGUAGES.find((x) => x.value === selectedLang)?.label || "English"}
                      </span>
                      <img src="/images/drop-arrow.png" alt="drop-arrow" />
                    </div>
                    <ul tabIndex={0} className="dropdown-content menu bg-base-100 rounded-box z-[1] w-full p-2 shadow-sm border border-zinc-100 mt-2">
                      {LANGUAGES.map((lang, i) => (
                        <li key={i}>
                          <button
                            onClick={() => {
                              changeLanguage(lang.value);
                              // We don't necessarily want to close the whole drawer here, 
                              // but let's at least ensure the dropdown loses focus
                              if (document.activeElement instanceof HTMLElement) {
                                document.activeElement.blur();
                              }
                            }}
                          >
                            {lang.label}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                </li>

                {/* Auth Buttons or Profile Section */}
                {(!userData?.token && !isGuest()) ? (
                  <div className="flex flex-col gap-3 mt-4">
                    <Link
                      to="/user/signup"
                      className="btn btn-outline w-full"
                      onClick={closeMobileDrawer}
                    >
                      {t("header.signup")}
                    </Link>
                    <Link
                      to="/user/login"
                      className="btn btn-pri w-full"
                      onClick={closeMobileDrawer}
                    >
                      {t("header.login")}
                    </Link>
                  </div>
                ) : (
                  <>
                    <div className="divider"></div>
                    {/* User Profile Summary in Drawer */}
                    <div className="px-2 py-4 flex items-center gap-3">
                      <AvatarImage
                        src={userData?.profile_image}
                        name={userData?.name}
                        alt="user"
                        style={{ width: "50px", height: "50px", objectFit: "cover", borderRadius: "50%" }}
                      />
                      <div className="flex flex-col">
                        <span className="headings-h6 text-zinc-800">
                          {userData?.name ? TrimText(userData.name, 20) : t("header.na")}
                        </span>
                        <span className="text-xs text-gray-500 capitalize">{userData?.role || "user"}</span>
                      </div>
                      {userData?.token && (
                        <button
                          className="ml-auto w-10 h-10 flex items-center justify-center rounded-full bg-[#FFC9C7] relative"
                          onClick={() => {
                            closeMobileDrawer();
                            Navigate("/user/notifications");
                          }}
                        >
                          <img src="/images/bell.svg" alt="bell" className="w-5" />
                          {unreadCount > 0 && (
                            <span className="absolute -top-1 -right-1 w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-bold flex items-center justify-center rounded-full border-2 border-white">
                              {unreadCount > 99 ? '99+' : unreadCount}
                            </span>
                          )}
                        </button>
                      )}
                    </div>


                    {String(globalData?.show_user_stats) !== "false" && (userData?.isAdvertiser || userData?.role === "advertiser") && (
                      <div className="mx-4 mb-2 space-y-3">
                        <div className="px-4 py-3 bg-gradient-to-r from-orange-50 to-red-50 rounded-lg border border-orange-100 flex items-center justify-between shadow-sm">
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <Clock className="w-5 h-5 text-orange-500" />
                            <span className="text-sm font-semibold text-orange-700 whitespace-nowrap">{t("header.hours")}:</span>
                          </div>
                          <span className="text-base font-bold text-orange-600 whitespace-nowrap">
                            {secToHours(userData?.remaining_hours || 0)}
                          </span>
                        </div>
                        <div className="px-4 py-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-100 flex items-center justify-between shadow-sm">
                          <div className="flex items-center gap-2">
                            <MousePointer2 className="w-5 h-5 text-blue-500" />
                            <span className="text-sm font-semibold text-blue-700">{t("header.ai_clicks")}:</span>
                          </div>
                          <span className="text-base font-bold text-blue-600">
                            {userData?.remaining_ai_clicks ?? 0}
                          </span>
                        </div>
                      </div>
                    )}

                    <div className="divider !my-0"></div>

                    {/* Profile Menu Items in Drawer */}
                    <div className="flex flex-col">
                      {(userData?.role === "advertiser" ? ADVERTISER_ITEMS : EFFECTIVE_USER_ITEMS).map((item, i) => (
                        item.label === "__divider__" ? (
                          <div key={`div-mobile-${i}`} className="px-4 py-2 bg-gray-50 border-y border-gray-100 mt-2 mb-1">
                            <span className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">{t("header.business")}</span>
                          </div>
                        ) : item.label === "__divider__staff" ? (
                          <div key={`div-staff-mobile-${i}`} className="px-4 py-2 bg-gray-50 border-y border-gray-100 mt-2 mb-1">
                            <span className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">{t("header.staff_access")}</span>
                          </div>
                        ) : item.label === "__rejected__" ? (
                          <div key={`rej-mobile-${i}`} className="flex flex-col gap-1 px-4 py-3 bg-red-50/90 border-y border-red-200/80 my-1">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-[6px]">
                                <span className="body-sm text-red-700 font-semibold">Application Rejected</span>
                              </div>
                              <Link
                                to="/advertiser/profile"
                                onClick={closeMobileDrawer}
                                className="px-3 py-1 text-xs font-semibold text-white bg-red-500 hover:bg-red-600 rounded-md transition-colors !no-underline shadow-sm"
                              >
                                <span>Fix & Reapply</span>
                              </Link>
                            </div>
                            {userData?.failed_verification_reason && (
                              <p className="text-[11px] text-red-600 mb-0 italic">Reason: {userData.failed_verification_reason}</p>
                            )}
                          </div>
                        ) : item.label === "__awaiting__" ? (
                          <div key={`await-mobile-${i}`} className="flex items-center justify-between px-4 py-3 bg-amber-50/80 border-y border-amber-200/70 my-1">
                            <div className="flex items-center gap-[10px]">
                              <img src="/images/my-a-icon4.svg" alt="pending" className="w-[18px]" />
                              <span className="body-sm text-amber-700 font-semibold">{t("header.awaiting_approval")}</span>
                            </div>
                            <Link
                              to="/advertiser/profile"
                              onClick={closeMobileDrawer}
                              className="px-3 py-1 text-xs font-semibold text-white bg-[#FF7E79] hover:bg-[#ff6560] rounded-md transition-colors !no-underline shadow-sm"
                            >
                              {t("common.edit", "Edit")}
                            </Link>
                          </div>
                        ) : item.label === "__become_advertiser__" ? (
                          <button
                            key={`become-mobile-${i}`}
                            onClick={() => {
                              closeMobileDrawer();
                              handleBecomeAdvertiser();
                            }}
                            className="flex items-center gap-[10px] px-4 py-3 hover:bg-gray-50 transition"
                          >
                            <img src="/images/my-a-icon4.svg" alt="become advertiser" className="w-[18px]" />
                            <span className="body-sm text-[#FF7E79] medium font-semibold">{t("header.apply_for_business")}</span>
                          </button>
                        ) : (
                          <li key={`item-mobile-${i}`}>
                            {item.to ? (
                              <Link
                                to={item.to}
                                onClick={closeMobileDrawer}
                                className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition border-none rounded-none w-full text-left !no-underline"
                              >
                                <span className="w-5 h-5 flex items-center justify-center shrink-0">
                                  {item.icon ? (
                                    item.icon
                                  ) : (
                                    <img src={item.image} alt={item.label} className="w-4 h-4 object-contain" />
                                  )}
                                </span>
                                <span className="text-sm font-medium text-zinc-700 truncate">{item.label}</span>
                              </Link>
                            ) : (
                              <button
                                type="button"
                                onClick={() => {
                                  closeMobileDrawer();
                                  item.onClick();
                                }}
                                className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition border-none rounded-none w-full text-left"
                              >
                                <span className="w-5 h-5 flex items-center justify-center shrink-0">
                                  {item.icon ? (
                                    item.icon
                                  ) : (
                                    <img src={item.image} alt={item.label} className="w-4 h-4 object-contain" />
                                  )}
                                </span>
                                <span className="text-sm font-medium text-zinc-700 truncate">{item.label}</span>
                              </button>
                            )}
                          </li>
                        )
                      ))}
                    </div>
                  </>
                )}
              </ul>
            </div>
          </div>
        </div>
      </header>

      <dialog id="select_role_modal" className="modal p-[15px]">
        <div className="modal-box max-w-[700px] !w-full">
          <SelectUser type={type} onClose={closeModal} />
        </div>
        <form method="dialog">
          {/* if there is a button in form, it will close the modal */}
          <button className="bg-white subtitle-2 flex items-center gap-[5px] px-[15px] py-[10px] rounded-full">
            <img src="/images/close.svg" alt="close" />
            <span className="subtitle-2 bold">{t("common.close")}</span>
          </button>
        </form>
      </dialog>

      <dialog id="delete_account_modal" className="modal p-[15px]">
        <div className="modal-box max-w-[515px] !w-full">
          <DeleteAccount onClose={closeDeleteAccountModal} />
        </div>
        <form method="dialog">
          {/* if there is a button in form, it will close the modal */}
          <button className="bg-white subtitle-2 flex items-center gap-[5px] px-[15px] py-[10px] rounded-full">
            <img src="/images/close.svg" alt="close" />
            <span className="subtitle-2 bold">{t("common.close")}</span>
          </button>
        </form>
      </dialog>

      <dialog id="logout_modal" className="modal p-[15px]">
        <div className="modal-box max-w-[515px] !w-full">
          <LogOut onClose={closeLogOutModal} />
        </div>
        <form method="dialog">
          {/* if there is a button in form, it will close the modal */}
          <button className="bg-white subtitle-2 flex items-center gap-[5px] px-[15px] py-[10px] rounded-full">
            <img src="/images/close.svg" alt="close" />
            <span className="subtitle-2 bold">{t("common.close")}</span>
          </button>
        </form>
      </dialog>

      {/* Tutorial Video Modal Popup */}
      {isTutorialModalOpen && (
        <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in">
          <div className="bg-white rounded-2xl max-w-3xl w-full p-5 md:p-6 shadow-2xl relative border border-zinc-100 overflow-hidden">
            {/* Modal Header */}
            <div className="flex items-center justify-between pb-3 mb-4 border-b border-zinc-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center text-[#FF7E79]">
                  <Video size={22} />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-zinc-900 leading-tight">
                    {tutorialModalType === "youtube"
                      ? (t("header.youtube_tutorial") || "You Tube Setup Guide")
                      : tutorialModalType === "twitch"
                      ? (t("header.twitch_tutorial") || "Twitch Tutorial")
                      : (t("header.product_tutorial") || "Product Tutorial")}
                  </h3>
                  {/* <p className="text-xs text-zinc-500">
                    {tutorialModalType === "youtube"
                      ? "Watch this video to learn how to set up and stream on YouTube effectively"
                      : tutorialModalType === "twitch"
                      ? "Watch this video to learn how to set up and stream on Twitch effectively"
                      : "Watch this video to learn how to create and manage products effectively"}
                  </p> */}
                </div>
              </div>
              <button
                onClick={() => setIsTutorialModalOpen(false)}
                className="p-2 rounded-full hover:bg-zinc-100 text-zinc-500 transition-colors"
                aria-label="Close modal"
              >
                <X size={20} />
              </button>
            </div>

            {/* Video Container */}
            <div className="w-full bg-black rounded-xl overflow-hidden shadow-inner max-h-[70vh] flex items-center justify-center">
              {tutorialVideoUrl ? (
                tutorialVideoUrl.includes("youtube.com") || tutorialVideoUrl.includes("youtu.be") ? (
                  <div className="relative pt-[56.25%] w-full">
                    <iframe
                      className="absolute top-0 left-0 w-full h-full"
                      src={tutorialVideoUrl.replace("watch?v=", "embed/")}
                      title="Create Product Tutorial"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                ) : (
                  <video
                    controls
                    autoPlay
                    src={tutorialVideoUrl}
                    className="w-full max-h-[480px] object-contain"
                  >
                    Your browser does not support the video tag.
                  </video>
                )
              ) : (
                <div className="text-center py-16 px-4 bg-zinc-900 text-white w-full">
                  <Video size={48} className="mx-auto text-zinc-600 mb-3" />
                  <p className="font-semibold text-zinc-300">No tutorial video uploaded yet.</p>
                  <p className="text-xs text-zinc-500 mt-1">Please upload a tutorial video in Admin Settings.</p>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="mt-4 pt-3 flex justify-end items-center border-t border-zinc-100">
              <button
                onClick={() => setIsTutorialModalOpen(false)}
                className="px-5 py-2 bg-zinc-100 hover:bg-zinc-200 text-zinc-700 text-xs font-semibold rounded-lg transition-colors"
              >
                {t("common.close") || "Close"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default Header;
